import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import AxiosInstance from "../../../shared/utils/axios";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";
import { useHydratePlotDesign, useNewTrialState, useTrial } from "../legacy";
import { RATES_DOSAGE_STEP } from "../constants";

export interface UseTrialBootstrapResult {
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

const useTrialBootstrap = (): UseTrialBootstrapResult => {
  const { id } = useParams();
  const { orgId, workspaceId } = useTrialsRouter();

  const {
    trialInfo,
    setTrialInfo,
    setCurrent,
    trialProtocolData,
    setTrialProtocolData,
  } = useNewTrialState();

  const trialQuery = useTrial(id);

  // Avoid hydrating stale cached Plot Design data during a refetch.
  const trialData = trialQuery.isFetching ? undefined : trialQuery.data;

  useHydratePlotDesign(trialData);

  useEffect(() => {
    setCurrent(RATES_DOSAGE_STEP);
  }, [setCurrent]);

  useEffect(() => {
    if (trialData && trialData !== trialInfo) {
      setTrialInfo(trialData);
    }
  }, [trialData, trialInfo, setTrialInfo]);

  // Derive the protocol from this request, not a potentially stale
  // trialInfo value left over from a previously opened trial.
  const rawProtocolId: unknown = trialQuery.data?.protocol_id;
  const protocolId =
    typeof rawProtocolId === "string" && rawProtocolId.length > 0
      ? rawProtocolId
      : undefined;

  const protocolQuery = useQuery<NonNullable<typeof trialProtocolData>>({
    queryKey: [
      "ratesDosageProtocol",
      orgId ?? "",
      workspaceId ?? "",
      protocolId,
    ],
    enabled:
      Boolean(id && protocolId) &&
      !trialQuery.isFetching &&
      !trialQuery.isError,
    queryFn: async () => {
      if (!protocolId) {
        throw new Error("Missing protocol ID");
      }

      const response = await AxiosInstance.get<
        NonNullable<typeof trialProtocolData>
      >(`protocol/${protocolId}`, {
        headers: {
          "org-id": orgId ?? "",
          "workspace-id": workspaceId ?? "",
        },
      });

      return response.data;
    },
    retry: false,
  });

  useEffect(() => {
    if (protocolQuery.data && protocolQuery.data !== trialProtocolData) {
      setTrialProtocolData(protocolQuery.data);
    }
  }, [protocolQuery.data, trialProtocolData, setTrialProtocolData]);

  const isError =
    !id || trialQuery.isError || (Boolean(protocolId) && protocolQuery.isError);

  const isLoading =
    trialQuery.isFetching ||
    (Boolean(id) && trialQuery.isLoading) ||
    (!trialQuery.isError &&
      Boolean(protocolId) &&
      (protocolQuery.isPending || protocolQuery.isFetching));

  const retry = (): void => {
    if (!id) {
      return;
    }

    if (trialQuery.isError || !trialQuery.data) {
      // The protocol query will become enabled when the trial recovers.
      void trialQuery.refetch();
      return;
    }

    if (protocolId) {
      void protocolQuery.refetch();
    } else {
      void trialQuery.refetch();
    }
  };

  return {
    isLoading,
    isError,
    retry,
  };
};

export default useTrialBootstrap;
